/**
 * NestedCategoryBoard.tsx
 *
 * CategoryBoard one level deeper: categories › records › child records on one
 * page (Admin > Procedures: categories › sub-categories › procedures).
 * Categories are listed in the order the page gives them (A–Z); a category's
 * records and a record's children are listed A–Z.
 *
 * Drag and drop files a row under another parent — it never orders (2026-10-07,
 * user: the boards assign, they no longer order; Prospera's pickers are
 * alphabetical and searchable):
 *
 *   - drag a record by its grip onto another category;
 *   - drag a child by its grip onto another record, in any category. Holding
 *     it over a closed category opens that category, so its records can take
 *     the drop.
 *
 * The moved row goes last in its new parent's saved order, so the rows already
 * there keep theirs (`onReorderRecords` / `onReorderChildren` get the parent's
 * rows in the order the page gave them, plus the moved one at the end). Each
 * drop saves at once and snaps back if the save fails (applyThenSave).
 *
 * Categories and records start collapsed. `expandAll` opens everything (a
 * searched view, where drag is off anyway). Rows keep CategoryBoard's grip and
 * actions slots, so a column header lines up with the child rows.
 *
 * On a Locked Admin page (useAdminPageLevel) drag is off — no grips, and a
 * drop saves nothing — and the page's AdminRowActions render nothing; rows
 * still open and close (R8, R18).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { ADMIN_CHILD_ROW_CLASS, ADMIN_PARENT_ROW_CLASS } from './AdminList';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { ACTIONS_SLOT, DROP_TARGET_CLASS, GRIP_SLOT, applyThenSave, innermostUnderPointer, type BoardCategory } from './CategoryBoard';
import { buildBoardState, compareTitles, containerOf, moveRecord, recordSave, type BoardState } from './categoryBoardState';

export interface NestedCategoryBoardProps<R, C> {
  /** In display order (the page sorts them A–Z). */
  categories: BoardCategory[];
  /** Each category's records, in their saved order. */
  records: R[];
  getRecordId: (record: R) => number;
  getRecordCategoryId: (record: R) => number;
  /** A category's records are listed A–Z by this. */
  getRecordTitle: (record: R) => string;
  /** Each record's children, in their saved order. */
  childRecords: C[];
  getChildId: (child: C) => number;
  getChildRecordId: (child: C) => number;
  /** A record's children are listed A–Z by this. */
  getChildTitle: (child: C) => string;
  /**
   * A record's row, beside its chevron (a click on it opens / closes the
   * record). Inline content only: it sits inside the toggle button.
   */
  renderRecord: (record: R) => React.ReactNode;
  renderChild: (child: C) => React.ReactNode;
  /** A sticky header row, laid out like renderChild's row. */
  columnHeader?: React.ReactNode;
  renderCategoryActions?: (category: BoardCategory) => React.ReactNode;
  renderRecordActions?: (record: R) => React.ReactNode;
  renderChildActions?: (child: C) => React.ReactNode;
  isRecordDeleted?: (record: R) => boolean;
  isChildDeleted?: (child: C) => boolean;
  /** The category a record was dropped on, with its records in saved order and the moved one last. */
  onReorderRecords: (categoryId: number, recordIds: number[]) => Promise<unknown>;
  /** The record a child was dropped on, with its children in saved order and the moved one last. */
  onReorderChildren: (recordId: number, childIds: number[]) => Promise<unknown>;
  onSaveError?: (error: unknown) => void;
  /** Singular and plural, for a category's count. */
  recordNoun?: [string, string];
  dragDisabled?: boolean;
  /** Every category and record open (a searched view). */
  expandAll?: boolean;
}

type Level = 'rec' | 'child';

const dragId = (level: Level, id: number) => `${level}:${id}`;
const kindOf = (id: string | number) => String(id).split(':')[0];
const idOf = (id: string | number) => Number(String(id).split(':')[1]);

/** What each dragged level may land on. A child over a category only opens it. */
const TARGETS: Record<Level, string[]> = {
  rec: ['catdrop'],
  child: ['recdrop', 'catdrop'],
};
/** How long a dragged child hovers over a closed category before it opens. */
const OPEN_ON_HOVER_MS = 500;

export function NestedCategoryBoard<R, C>({
  categories,
  records,
  getRecordId,
  getRecordCategoryId,
  getRecordTitle,
  childRecords,
  getChildId,
  getChildRecordId,
  getChildTitle,
  renderRecord,
  renderChild,
  columnHeader,
  renderCategoryActions,
  renderRecordActions,
  renderChildActions,
  isRecordDeleted,
  isChildDeleted,
  onReorderRecords,
  onReorderChildren,
  onSaveError,
  recordNoun = ['item', 'items'],
  dragDisabled: dragDisabledProp = false,
  expandAll = false,
}: NestedCategoryBoardProps<R, C>) {
  // A Locked page never drags.
  const locked = useAdminPageLevel() === 'locked';
  const dragDisabled = dragDisabledProp || locked;
  const recordById = useMemo(() => new Map(records.map((r) => [getRecordId(r), r])), [records, getRecordId]);
  const childById = useMemo(() => new Map(childRecords.map((c) => [getChildId(c), c])), [childRecords, getChildId]);

  // Categories › records, each list in saved order (what a move saves).
  const recordsFromProps = useMemo((): BoardState => {
    const known = new Set(categories.map((c) => c.id));
    const byCategory: Record<number, number[]> = {};
    for (const r of records) {
      const cat = getRecordCategoryId(r);
      if (known.has(cat)) (byCategory[cat] ??= []).push(getRecordId(r));
    }
    return buildBoardState(categories.map((c) => c.id), byCategory, []);
  }, [categories, records, getRecordCategoryId, getRecordId]);

  // Records › children, the same.
  const childrenFromProps = useMemo((): BoardState => {
    const recordIds = records.map(getRecordId);
    const known = new Set(recordIds);
    const byRecord: Record<number, number[]> = {};
    for (const c of childRecords) {
      const rec = getChildRecordId(c);
      if (known.has(rec)) (byRecord[rec] ??= []).push(getChildId(c));
    }
    return buildBoardState(recordIds, byRecord, []);
  }, [records, getRecordId, childRecords, getChildRecordId, getChildId]);

  const [recState, setRecState] = useState<BoardState>(recordsFromProps);
  const [childState, setChildState] = useState<BoardState>(childrenFromProps);
  const [openCategories, setOpenCategories] = useState<Set<number>>(new Set());
  const [openRecords, setOpenRecords] = useState<Set<number>>(new Set());
  const [active, setActive] = useState<{ level: Level; id: number } | null>(null);
  const hoverOpen = useRef<{ categoryId: number; timer: ReturnType<typeof setTimeout> } | null>(null);

  useEffect(() => setRecState(recordsFromProps), [recordsFromProps]);
  useEffect(() => setChildState(childrenFromProps), [childrenFromProps]);
  useEffect(() => () => clearHoverOpen(), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const collisions = useMemo(() => {
    const byLevel = {
      rec: innermostUnderPointer((id) => TARGETS.rec.includes(kindOf(id))),
      child: innermostUnderPointer((id) => TARGETS.child.includes(kindOf(id))),
    };
    const detect: CollisionDetection = (args) => byLevel[kindOf(args.active.id) as Level]?.(args) ?? [];
    return detect;
  }, []);

  function clearHoverOpen() {
    if (hoverOpen.current) clearTimeout(hoverOpen.current.timer);
    hoverOpen.current = null;
  }

  // A dragged child held over a closed category opens it.
  function handleDragOver(event: DragOverEvent) {
    if (dragDisabled) return;
    const overId = event.over ? String(event.over.id) : '';
    const categoryId = kindOf(event.active.id) === 'child' && kindOf(overId) === 'catdrop' ? idOf(overId) : null;
    if (hoverOpen.current?.categoryId === categoryId) return;
    clearHoverOpen();
    if (categoryId === null || openCategories.has(categoryId)) return;
    hoverOpen.current = {
      categoryId,
      timer: setTimeout(() => {
        setOpenCategories((prev) => new Set(prev).add(categoryId));
        hoverOpen.current = null;
      }, OPEN_ON_HOVER_MS),
    };
  }

  function handleDragEnd(event: DragEndEvent) {
    clearHoverOpen();
    setActive(null);
    // Drag off (a searched view, or a Locked page): a drop moves and saves nothing.
    if (dragDisabled) return;
    const level = kindOf(event.active.id) as Level;
    const overId = event.over ? String(event.over.id) : '';
    const wanted = level === 'rec' ? 'catdrop' : 'recdrop';
    if (kindOf(overId) !== wanted) return;

    const isRecord = level === 'rec';
    const current = isRecord ? recState : childState;
    const set = isRecord ? setRecState : setChildState;
    const id = idOf(event.active.id);
    const to = String(idOf(overId));
    if (!(to in current.records) || containerOf(current, id) === to) return;

    // Filed last in the new parent's saved order.
    const after = moveRecord(current, id, to, current.records[to].length);
    const save = recordSave(current, after, id);
    if (!save) return;
    void applyThenSave(
      () => set(after),
      () => (isRecord ? onReorderRecords : onReorderChildren)(save.categoryId, save.ids),
      () => set(current),
      onSaveError,
    );
  }

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleCategory = toggleIn(setOpenCategories);
  const toggleRecord = toggleIn(setOpenRecords);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const count = (n: number) => `${n} ${n === 1 ? recordNoun[0] : recordNoun[1]}`;

  const byTitle = <T,>(ids: number[], byId: Map<number, T>, title: (row: T) => string) => {
    const t = (id: number) => {
      const row = byId.get(id);
      return row === undefined ? '' : title(row);
    };
    return [...ids].sort((a, b) => compareTitles(t(a), t(b)) || a - b);
  };

  const activeContainer =
    active === null ? null : containerOf(active.level === 'rec' ? recState : childState, active.id);

  function childList(recordId: number) {
    const ids = byTitle(childState.records[String(recordId)] ?? [], childById, getChildTitle);
    return (
      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {ids.map((id) => {
          const child = childById.get(id);
          if (child === undefined) return null;
          const deleted = !!isChildDeleted?.(child);
          return (
            <DragRow key={id} dragKey={dragId('child', id)} disabled={dragDisabled || deleted}>
              {(drag) => (
                <li
                  ref={drag.setNodeRef}
                  className={cn(
                    'flex items-center gap-2 py-0 pl-10 pr-3 text-sm',
                    ADMIN_CHILD_ROW_CLASS,
                    deleted && 'bg-slate-50 hover:bg-slate-50',
                    active?.level === 'child' && active.id === id && 'opacity-40',
                  )}
                  data-child-id={id}
                  data-deleted={deleted || undefined}
                >
                  <span className={GRIP_SLOT}>{!dragDisabled && !deleted && drag.grip}</span>
                  <div className={cn('min-w-0 flex-1', deleted && 'opacity-60')}>{renderChild(child)}</div>
                  <div className={cn(ACTIONS_SLOT, 'py-1')}>
                    {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
                    {renderChildActions?.(child)}
                  </div>
                </li>
              )}
            </DragRow>
          );
        })}
      </ul>
    );
  }

  function recordList(categoryId: number) {
    const ids = byTitle(recState.records[String(categoryId)] ?? [], recordById, getRecordTitle);
    return (
      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {ids.map((id) => {
          const record = recordById.get(id);
          if (record === undefined) return null;
          const deleted = !!isRecordDeleted?.(record);
          const hasChildren = (childState.records[String(id)] ?? []).length > 0;
          const open = hasChildren && (expandAll || openRecords.has(id));
          return (
            <DropItem
              key={id}
              dropId={`recdrop:${id}`}
              disabled={dragDisabled || deleted}
              highlight={active?.level === 'child' && activeContainer !== String(id)}
            >
              <DragRow dragKey={dragId('rec', id)} disabled={dragDisabled || deleted}>
                {(drag) => (
                  <ParentHeader
                    nodeRef={drag.setNodeRef}
                    className={cn(
                      'px-3 py-1 text-sm',
                      deleted ? 'bg-slate-50' : 'bg-slate-50 hover:bg-slate-100 transition-colors',
                      hasChildren && 'cursor-pointer',
                      active?.level === 'rec' && active.id === id && 'opacity-40',
                    )}
                    grip={!dragDisabled && !deleted ? drag.grip : null}
                    hasChildren={hasChildren}
                    open={open}
                    onToggle={() => toggleRecord(id)}
                    content={<span className={cn('flex min-w-0 flex-1 items-center gap-2', deleted && 'opacity-60')}>{renderRecord(record)}</span>}
                    actions={
                      <>
                        {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
                        {renderRecordActions?.(record)}
                      </>
                    }
                    data-record-id={id}
                  />
                )}
              </DragRow>
              {open && childList(id)}
            </DropItem>
          );
        })}
      </ul>
    );
  }

  const activeRow =
    active === null
      ? null
      : active.level === 'rec'
        ? (() => {
            const record = recordById.get(active.id);
            return record === undefined ? null : renderRecord(record);
          })()
        : (() => {
            const child = childById.get(active.id);
            return child === undefined ? null : renderChild(child);
          })();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisions}
      // Rows mount while dragging (a category opened by hovering): measure them as they come.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(event: DragStartEvent) => setActive({ level: kindOf(event.active.id) as Level, id: idOf(event.active.id) })}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        clearHoverOpen();
        setActive(null);
      }}
    >
      <div className="space-y-2" data-testid="nested-category-board">
        {columnHeader && (
          <div className="sticky top-0 z-20 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs font-semibold whitespace-nowrap text-slate-600">
            <span className={GRIP_SLOT} aria-hidden="true" />
            <div className="min-w-0 flex-1">{columnHeader}</div>
            <span className={ACTIONS_SLOT}>{locked ? '' : 'Actions'}</span>
          </div>
        )}
        {recState.categoryIds.map((categoryId) => {
          const category = categoryById.get(categoryId) ?? { id: categoryId, title: '' };
          const n = (recState.records[String(categoryId)] ?? []).length;
          const open = n > 0 && (expandAll || openCategories.has(categoryId));
          return (
            <DropItem
              key={categoryId}
              as="section"
              label={category.title}
              dropId={`catdrop:${categoryId}`}
              disabled={dragDisabled || !!category.deleted}
              highlight={active?.level === 'rec' && activeContainer !== String(categoryId)}
              className="rounded-lg border border-slate-200 bg-white"
            >
              <ParentHeader
                className={cn('rounded-lg px-3 py-2', n > 0 ? ADMIN_PARENT_ROW_CLASS : 'bg-slate-100 transition-colors', open && 'rounded-b-none')}
                grip={null}
                hasChildren={n > 0}
                open={open}
                onToggle={() => toggleCategory(categoryId)}
                content={
                  <>
                    <span className={cn('truncate font-medium', category.deleted ? 'text-slate-400 line-through' : 'text-slate-900')}>
                      {category.title}
                    </span>
                    <Badge variant="neutral" size="sm">{count(n)}</Badge>
                    {category.deleted && <Badge variant="error" size="sm">Deleted</Badge>}
                  </>
                }
                actions={renderCategoryActions?.(category)}
              />
              {open && recordList(categoryId)}
            </DropItem>
          );
        })}
      </div>

      {/* The dragged row floats under the pointer; the lists stay put. */}
      <DragOverlay dropAnimation={null}>
        {activeRow !== null ? (
          <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm shadow-lg">
            <span className={GRIP_SLOT}>
              <GripVertical className="h-4 w-4 text-slate-400" />
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2">{activeRow}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

/**
 * A drop target: a category section, or a record row with its open children.
 * Highlighted while a row from another parent is over it.
 */
function DropItem({
  dropId,
  disabled,
  highlight,
  as = 'li',
  label,
  className,
  children,
}: {
  dropId: string;
  disabled: boolean;
  /** A row of the level this target takes is being dragged, from another parent. */
  highlight: boolean;
  as?: 'li' | 'section';
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId, disabled });
  const Tag = as;
  return (
    <Tag ref={setNodeRef} aria-label={label} className={cn(className, highlight && isOver && DROP_TARGET_CLASS)}>
      {children}
    </Tag>
  );
}

/** One draggable row; hands its grip (the only handle) and node ref to what it renders. */
function DragRow({
  dragKey,
  disabled,
  children,
}: {
  dragKey: string;
  disabled: boolean;
  children: (drag: { grip: React.ReactNode; setNodeRef: (node: HTMLElement | null) => void }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: dragKey, disabled });
  const grip = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
      aria-label="Drag to move"
      title="Drag onto another parent to move it there"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
  return <>{children({ grip, setNodeRef })}</>;
}

/** A category's or record's header row: a click anywhere opens / closes it. */
function ParentHeader({
  nodeRef,
  className,
  grip,
  hasChildren,
  open,
  onToggle,
  content,
  actions,
  ...rest
}: {
  nodeRef?: (node: HTMLElement | null) => void;
  className: string;
  grip: React.ReactNode;
  hasChildren: boolean;
  open: boolean;
  onToggle: () => void;
  content: React.ReactNode;
  actions?: React.ReactNode;
  'data-record-id'?: number;
}) {
  return (
    <div ref={nodeRef} onClick={hasChildren ? onToggle : undefined} className={cn('flex items-center gap-2', className)} {...rest}>
      <span className={GRIP_SLOT}>{grip}</span>
      {hasChildren ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
          {content}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="h-4 w-4 shrink-0" aria-hidden="true" />
          {content}
        </div>
      )}
      {actions && (
        <div className={ACTIONS_SLOT} onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  );
}
